<script lang="ts" setup>
import { computed } from 'vue'
import PlansSelfServeUsageItem from './PlansSelfServeUsageItem.vue'
import { usePlanStore } from './usePlanStore'
import type { CustomerPlan } from './planTypes'
import { Resource } from './planTypes'
import { getResourceLimit, getResourceUsage } from './limitAndUsage'

const store = usePlanStore()

const hasLicense = (plan: CustomerPlan, resource: Resource): boolean =>
  plan.plan_licenses.some((license) => license.resource === resource)

const usages = computed(() => {
  const plan = store.customerPlan
  if (!plan) {
    return []
  }

  const usages: { resourceName: string; usage: number; limit: number }[] = []

  if (hasLicense(plan, Resource.ANNOTATION_CREDIT)) {
    usages.push({
      resourceName: 'Credits',
      usage: getResourceUsage(plan, Resource.ANNOTATION_CREDIT),
      limit: getResourceLimit(plan, Resource.ANNOTATION_CREDIT),
    })
  }

  if (hasLicense(plan, Resource.STORAGE)) {
    usages.push({
      resourceName: 'Storage',
      usage: getResourceUsage(plan, Resource.STORAGE),
      limit: getResourceLimit(plan, Resource.STORAGE),
    })
  }

  if (hasLicense(plan, Resource.FILE)) {
    usages.push({
      resourceName: 'Files',
      usage: getResourceUsage(plan, Resource.FILE),
      limit: getResourceLimit(plan, Resource.FILE),
    })
  }
  if (hasLicense(plan, Resource.USER)) {
    usages.push({
      resourceName: 'Seats',
      usage: getResourceUsage(plan, Resource.USER),
      limit: getResourceLimit(plan, Resource.USER),
    })
  }

  if (hasLicense(plan, Resource.WORKSPACE)) {
    usages.push({
      resourceName: 'Workspaces',
      usage: getResourceUsage(plan, Resource.WORKSPACE),
      limit: getResourceLimit(plan, Resource.WORKSPACE),
    })
  }

  if (hasLicense(plan, Resource.VIDEO_FILE)) {
    usages.push({
      resourceName: 'Video Files',
      usage: getResourceUsage(plan, Resource.VIDEO_FILE),
      limit: getResourceLimit(plan, Resource.VIDEO_FILE),
    })
  }

  if (hasLicense(plan, Resource.PDF_FILE)) {
    usages.push({
      resourceName: 'PDF Files',
      usage: getResourceUsage(plan, Resource.PDF_FILE),
      limit: getResourceLimit(plan, Resource.PDF_FILE),
    })
  }

  if (hasLicense(plan, Resource.DICOM_FILE)) {
    usages.push({
      resourceName: 'DICOM Files',
      usage: getResourceUsage(plan, Resource.DICOM_FILE),
      limit: getResourceLimit(plan, Resource.DICOM_FILE),
    })
  }

  return usages
})
</script>

<template>
  <div class="usage-summary">
    <div class="usage-summary__header">
      <h3 class="usage-summary__header-title">Usages</h3>
      <p class="usage-summary__header-description">See your platform usage and limits</p>
    </div>
    <div class="usage-summary__list">
      <PlansSelfServeUsageItem
        v-for="usage in usages"
        :key="usage.resourceName"
        class="usage-summary__list__item"
        :resource-name="usage.resourceName"
        :limit="usage.limit"
        :usage="usage.usage"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.usage-summary {
  display: flex;
  flex-direction: column;
  gap: var(--size-4);

  &__header-title {
    @include typographyBoldBody200;
  }

  &__header-description {
    @include typographyRegularBody200;
    color: var(--color-text-subtlest);
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 0;

    &__item {
      max-width: 536px;
    }
  }
}
</style>
